/**
 * Bandit Referral Tracking System
 *
 * Captures referral codes from URL (?rp_code=REF_ID), validates them against the backend,
 * persists them in cookies, and ensures they're passed through to checkout.
 */

class BanditReferralTracker {
	constructor() {
		this.cookieName = "BANDIT_REF_ID";
		this.urlParam = "rp_code";
		this.attributionWindow = 30;

		const apiConfig = window.BANDIT_API_CONFIG || {};
		const apiDomain = apiConfig.domain || "member.benditrunning.com";
		this.apiEndpoint = `https://${apiDomain}/api/referrers/`;

		this.init();
	}

	init() {
		const urlReferralCode = this.getReferralCodeFromURL();

		if (urlReferralCode) {
			this.setReferralCodeCookie(urlReferralCode);
			this.validateAndStoreReferralCode(urlReferralCode);
		}

		this.ensureCheckoutIntegration();
	}

	getReferralCodeFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		const code = urlParams.get(this.urlParam);

		if (code) {
			return code.trim();
		}

		return null;
	}

	async validateAndStoreReferralCode(code) {
		try {
			const response = await fetch(
				`${this.apiEndpoint}${encodeURIComponent(code)}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				}
			);

			if (response.ok) {
				this.cleanURL();
				this.ensureCheckoutIntegration();
			} else {
				console.warn(
					"[Bandit Referral] Invalid referral code:",
					code,
					"Status:",
					response.status
				);
				this.deleteCookie();
			}
		} catch (error) {
			console.error("[Bandit Referral] Error validating referral code:", error);
		}
	}

	setReferralCodeCookie(code) {
		const expiryDate = new Date();
		expiryDate.setDate(expiryDate.getDate() + this.attributionWindow);

		const cookieValue = `${this.cookieName}=${encodeURIComponent(code)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
		document.cookie = cookieValue;
	}

	getReferralCodeFromCookie() {
		const cookies = document.cookie.split(";");

		for (let cookie of cookies) {
			const [name, value] = cookie.trim().split("=");
			if (name === this.cookieName) {
				return decodeURIComponent(value);
			}
		}

		return null;
	}

	deleteCookie() {
		document.cookie = `${this.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	}

	cleanURL() {
		if (window.history && window.history.replaceState) {
			const url = new URL(window.location.href);
			url.searchParams.delete(this.urlParam);
			window.history.replaceState({}, document.title, url.toString());
		}
	}

	ensureCheckoutIntegration() {
		const referralCode = this.getReferralCodeFromCookie();

		if (!referralCode) {
			return;
		}

		this.attachToCartForms(referralCode);
		this.attachToCheckoutRedirects(referralCode);
		this.monitorDOMForDynamicForms(referralCode);
	}

	attachToCartForms(referralCode) {
		const forms = document.querySelectorAll('form[action*="/cart"]');

		forms.forEach((form) => {
			this.addReferralInputToForm(form, referralCode);
		});
	}

	attachToCheckoutRedirects(referralCode) {
		document.addEventListener(
			"click",
			(e) => {
				const target = e.target.closest(
					'a[href*="/checkout"], button[data-checkout], [data-shopify-checkout]'
				);

				if (target) {
					this.handleCheckoutClick(target, referralCode);
				}
			},
			true
		);

		const originalFetch = window.fetch;
		window.fetch = async (...args) => {
			const url = args[0];
			if (
				typeof url === "string" &&
				(url.includes("/cart") || url.includes("/checkout"))
			) {
				this.injectReferralIntoRequest(args, referralCode);
			}
			return originalFetch.apply(window, args);
		};
	}

	handleCheckoutClick(element, referralCode) {
		if (element.tagName === "A") {
			const href = element.getAttribute("href");
			if (href && href.includes("/checkout")) {
				const url = new URL(href, window.location.origin);
				url.searchParams.set("attributes[Referrer Code]", referralCode);
				element.setAttribute("href", url.toString());
			}
		}
	}

	injectReferralIntoRequest(fetchArgs, referralCode) {
		const options = fetchArgs[1];

		if (options && options.body) {
			try {
				if (typeof options.body === "string") {
					const data = JSON.parse(options.body);

					if (!data.attributes) {
						data.attributes = {};
					}

					data.attributes["Referrer Code"] = referralCode;
					fetchArgs[1].body = JSON.stringify(data);
				} else if (options.body instanceof FormData) {
					options.body.append("attributes[Referrer Code]", referralCode);
				}
			} catch (e) {
				console.warn(
					"[Bandit Referral] Could not inject referral code into request:",
					e
				);
			}
		}
	}

	addReferralInputToForm(form, referralCode) {
		const existingInput = form.querySelector(
			'input[name="attributes[Referrer Code]"]'
		);

		if (existingInput) {
			existingInput.value = referralCode;
			return;
		}

		const input = document.createElement("input");
		input.type = "hidden";
		input.name = "attributes[Referrer Code]";
		input.value = referralCode;
		form.appendChild(input);
	}

	monitorDOMForDynamicForms(referralCode) {
		if (typeof MutationObserver === "undefined") {
			return;
		}

		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						if (
							node.tagName === "FORM" &&
							node.action &&
							node.action.includes("/cart")
						) {
							this.addReferralInputToForm(node, referralCode);
						}

						const forms =
							node.querySelectorAll &&
							node.querySelectorAll('form[action*="/cart"]');
						if (forms) {
							forms.forEach((form) =>
								this.addReferralInputToForm(form, referralCode)
							);
						}
					}
				});
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		window.banditReferralTracker = new BanditReferralTracker();
	});
} else {
	window.banditReferralTracker = new BanditReferralTracker();
}
